<template>
<div class="answer relative"
     @click="markAnswered"
     :class="{'answer--correct': isCorrect, 'answer--incorrect': isIncorrect}"
    >
    <div class="answer__content relative flex items-center" :data-letter="letter" >
        <AnswerImage :answer="answer" :selected-answer="selectedAnswer" :src="answerImage" />
        <p>{{ answer.label }}</p>
    </div>
    <div class="answer__shadow" ref="shadow">
        <span>{{ incorrectInfo }}</span>
    </div>
</div>
</template>

<script>
import AnswerImage from "./AnswerImage.vue";

export default {
	name: "AnswerElement",
	components: {AnswerImage},
	props: {
		index: Number,
		answer: Object,
		isAnswered: Boolean,
		selectedAnswer: Boolean,
		incorrectInfo: String,
		questionImage: String,
	},
	emits: ["answered"],
	computed: {
		letter() {
			return ["a.", "b.", "c.", "d.", "e.", "f."][this.index];
		},
		isCorrect() {
			return this.selectedAnswer && this.answer.is_correct;
		},
		isIncorrect() {
			return this.selectedAnswer && !this.answer.is_correct;
		},
		answerImage() {
			return `/img/illustrations/${this.questionImage}_${this.answer.image}.png`;
		}
	},
	methods: {
		scrollToShadow() {
			this.$refs.shadow.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});
		},
		markAnswered() {
			if (this.isAnswered) { return; }
			this.$emit("answered", this.answer);
			setTimeout(() => this.scrollToShadow(), 400);
		},
	}
};
</script>

<style scoped lang="scss">
.answer {
    --border-width: 2px;
    --border-radius: 10px;
    --padding: 0 20px;
    --text-size: 16px;
    --text-leading: 24px;
    --incorrect-text-size: 14px;
    --incorrect-text-leading: 22px;
    --incorrect-padding: 20px;
    --letter-text-size: 16px;
    --letter-size: 30px;
    --letter-border-radius: 10px 0px;
    --height: 130px;
    --shadow-offset: 6px;
    --shadow-color: var(--yellow);

    cursor: pointer;
    user-select: none;

    @media (min-width: 1024px) {
        --border-width: 4px;
        --border-radius: 20px;
        --padding: 0 32px;
        --text-size: 22px;
        --text-leading: 32px;
        --incorrect-text-size: 20px;
        --incorrect-text-leading: 30px;
        --incorrect-padding: 30px;
        --letter-text-size: 22px;
        --letter-size: 50px;
        --letter-border-radius: 20px 0px;
        --height: 260px;
        --shadow-offset: 10px;
    }

    &:active {
        .answer__content {
            transform: translate(var(--shadow-offset), var(--shadow-offset))
        }
    }

    .answer__content {
        position: relative;
        z-index: 1;
        border: var(--border-width) solid black;
        border-radius: var(--border-radius);
        height: var(--height);
        background: white;
        transition: transform .4s, background .4s;

        p {
            font-size: var(--text-size);
            line-height: var(--text-leading);
            padding: var(--padding);
        }

        &::before {
            content: attr(data-letter);
            position: absolute;;
            width: var(--letter-size);
            height: var(--letter-size);
            left: calc(-1 * var(--border-width));
            top: calc(-1 * var(--border-width));
            font-size: var(--letter-text-size);
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            border: var(--border-width) solid black;
            border-radius: var(--letter-border-radius);
            color: white;
            background: var(--light-blue);
            z-index: 2;
        }
    }

    .answer__shadow {
        position: relative;
        width: 100%;
        min-height: var(--height);
        border: var(--border-width) solid black;
        border-radius: var(--border-radius);
        background: var(--shadow-color);
        left: var(--shadow-offset);
        top: var(--shadow-offset);
        margin-top: calc(-1 * var(--height));
        font-size: var(--incorrect-text-size);
        line-height: var(--incorrect-text-leading);
        padding: var(--incorrect-padding);
        transition: padding-top .4s;
        overflow: hidden;

        span {
            transition: margin-top .4s;
            margin-top: calc(-1 * var(--height));
            display: block;
        }
    }

    &.answer--correct,
    &.answer--incorrect {
        p {
            font-weight: 700;
        }
    }

    &.answer--correct {
        .answer__content::before,
        .answer__content {
            background: var(--green);
        }
    }

    &.answer--incorrect {
        .answer__content::before,
        .answer__content {
            background: var(--red);
        }
        .answer__shadow {
            background: white;
            padding: calc(var(--height) + var(--incorrect-padding) * 0.6) var(--incorrect-padding) var(--incorrect-padding) var(--incorrect-padding);
            span {
                margin-top: 0;
            }
        }
    }

}
</style>
